import { AppRoute } from '@shared/constants';

import { removeSession, retrieveSession, storeSession } from './storage.utils';

/**
 * Session-storage key (via `storeSession`) for the in-app path to restore
 * after `/loading` finishes user bootstrap.
 */
const AUTH_RETURN_URL_KEY = 'authReturnUrl';

/**
 * True when `url` is an in-app path that should be restored after login or
 * a service-worker refresh, rather than the loading or login-error screens.
 *
 * @param url Candidate path, optionally including query string and hash.
 * @returns True when the path is safe to restore with `navigateByUrl`.
 */
export function isRestorableAuthReturnUrl(url: string): boolean {
  if (!url.startsWith('/') || url.startsWith('//')) {
    return false;
  }

  const path = url.split(/[?#]/)[0];

  if (path === '/' || path === '') {
    return false;
  }

  return !path.endsWith(AppRoute.Loading) && !path.endsWith(AppRoute.LoginUnsuccessful);
}

/**
 * Remembers the current in-app path so `/loading` can send the user back
 * instead of the default home/main-menu page.
 *
 * @param url Router URL from a guard `state.url` or `location`.
 */
export function stashAuthReturnUrl(url: string): void {
  const trimmed = url.trim();
  if (!isRestorableAuthReturnUrl(trimmed)) {
    return;
  }

  storeSession(AUTH_RETURN_URL_KEY, trimmed);
}

/**
 * Drops any stashed in-app path without restoring it.
 */
export function clearAuthReturnUrl(): void {
  removeSession(AUTH_RETURN_URL_KEY);
}

/**
 * Reads and clears the stashed in-app path.
 *
 * @returns Path to restore, or `null` when none is pending.
 */
export function consumeAuthReturnUrl(): string | null {
  const value = retrieveSession<string>(AUTH_RETURN_URL_KEY);
  clearAuthReturnUrl();

  const trimmed = value?.trim();
  if (!trimmed || !isRestorableAuthReturnUrl(trimmed)) {
    return null;
  }

  return trimmed;
}
